import React, { useState } from 'react';
import { Spinner, Alert, Button,Pagination } from 'flowbite-react';
import { FaFileAlt, FaPlus } from 'react-icons/fa';
import api from '../../../services/api/axiosConfig';
import { Link } from 'react-router-dom';
import UploadCustomerDocument from './UploadCustomerDocument';
import API_ENDPOINTS, { buildEndpoint } from '../../../services/api/endpoints';
import { toast } from 'react-toastify';

const CustomerDocumentListing = ({ customerId, documents, loading, error,pagination,onPageChange, onDeleteDocument ,onDocumentStatusChange }) => {

    // Separate state for tracking loading of preview and download actions
    const [previewLoadingId, setPreviewLoadingId] = useState(null); // Track which doc is being previewed
    const [downloadLoadingId, setDownloadLoadingId] = useState(null); // Track which doc is being downloaded
    const [downloadError, setDownloadError] = useState(null);       // Local error for download/preview
    const [isUploadModalOpen, setIsUploadModalOpen] = useState(false);

    // New state for verify/unverify loading and error
    const [statusChangeLoadingId, setStatusChangeLoadingId] = useState(null);
    const [statusChangeError, setStatusChangeError] = useState(null);
    const [statusChangeSuccess, setStatusChangeSuccess] = useState(null); // Added success state

  // Define document verification statuses
    const STATUS_VERIFIED = 1;
    const STATUS_UNVERIFIED = 0;

    const formatFileSize = (bytes) => {
        if (bytes === 0) return '0 Bytes';
        const k = 1024;
        const sizes = ['Bytes', 'KB', 'MB', 'GB', 'TB'];
        const i = Math.floor(Math.log(bytes) / Math.log(k));
        return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
    };

    const fetchAndHandleFile = async (docId, fileName, type = 'preview') => {
        console.log(`Attempting to ${type} document: ${fileName} (ID: ${docId})`); // Debug log

        setDownloadError(null); // Clear previous errors

        if (type == 'preview') {
            setPreviewLoadingId(docId); // Set loading for preview
        } else {
            setDownloadLoadingId(docId); // Set loading for download
        }

        try {

            const url = (type =='preview') ? buildEndpoint(API_ENDPOINTS.PANEL.CUSTOMER.DOCUMENT.PREVIEW,{customerId,id:docId})
                       : buildEndpoint(API_ENDPOINTS.PANEL.CUSTOMER.DOCUMENT.DOWNLOAD,{customerId,id:docId});
            
            const response = await api.get(url, {
                responseType: 'blob',
            });

            const fileBlob = new Blob([response.data], { type: response.headers['content-type'] });
            const fileUrl = window.URL.createObjectURL(fileBlob);

            if (type === 'preview') {
                window.open(fileUrl, '_blank');
            } else {
                const link = document.createElement('a');
                link.href = fileUrl;
                link.setAttribute('download', fileName);
                document.body.appendChild(link);
                link.click();
                document.body.removeChild(link);
            }
            window.URL.revokeObjectURL(fileUrl);

        } catch (err) {
            console.error(`Error ${type}ing document:`, err);
            if (err.response) {
                if (err.response.data instanceof Blob) {
                    const reader = new FileReader();
                    reader.onload = function () {
                        try {
                            const errorObj = JSON.parse(reader.result);
                            setDownloadError(errorObj.message || `Failed to ${type} document. Server responded with ${err.response.status}.`);
                        } catch (e) {
                            setDownloadError(`Failed to ${type} document. Server responded with status ${err.response.status}.`);
                        }
                    };
                    reader.readAsText(err.response.data);
                } else {
                    setDownloadError(err.response.data.message || `Failed to ${type} document. Status: ${err.response.status}`);
                }
            } else if (err.request) {
                setDownloadError(`Network Error: No response from server when trying to ${type} document. Please check your internet connection.`);
            } else {
                setDownloadError(`An unexpected error occurred while trying to ${type} document: ${err.message}`);
            }
        } finally {
            if (type === 'preview') {
                setPreviewLoadingId(null); // Clear loading for preview
            } else {
                setDownloadLoadingId(null); // Clear loading for download
            }
        }
    };


    const handleVerifyUnverify = async (docId, action) => {
        setStatusChangeError(null);
        setStatusChangeSuccess(null); // Clear previous success
        setStatusChangeLoadingId(docId);

        try {
            
            const url = (action=='verify')? buildEndpoint(API_ENDPOINTS.PANEL.CUSTOMER.DOCUMENT.VERIFY,{customerId,docId}): 
                        buildEndpoint(API_ENDPOINTS.PANEL.CUSTOMER.DOCUMENT.UNVERIFY,{customerId,docId});
            
            const response = await api.get(url); // Using your imported 'api'
            console.log(response);
            if (response.status == 200) {
               toast.success(
                    action === 'verify'
                        ? 'Document verified successfully!'
                        : 'Document verification removed successfully!'
                    ); 
                
                if (onDocumentStatusChange) {
                    onDocumentStatusChange(docId, action === 'verify' ? STATUS_VERIFIED : STATUS_UNVERIFIED);
                }               
                // You might also want a global success notification here
                console.log(`Document ${action}ed successfully.`);
            } else {
                setStatusChangeError(response.data.message || `Failed to ${action} document. Status: ${response.status}`);
            }
        } catch (err) {
            console.error(`Error ${action}ing document:`, err);
            if (err.response) {
                setStatusChangeError(err.response.data.message || `Failed to ${action} document. Status: ${err.response.status}`);
            } else if (err.request) {
                setStatusChangeError(`Network Error: No response from server when trying to ${action} document.`);
            } else {
                setStatusChangeError(`An unexpected error occurred while trying to ${action} document: ${err.message}`);
            }
        } finally {
            setStatusChangeLoadingId(null);
        }
    };


    return (
        <>

            {statusChangeError && (
                <Alert color="failure" onDismiss={() => setStatusChangeError(null)} className="mb-4">
                <span className="font-medium">Status Change Error:</span> {statusChangeError}
                </Alert>
            )}
            
            {statusChangeSuccess && (
                <Alert color="success" onDismiss={() => setStatusChangeSuccess(null)} className="mb-4">
                <span className="font-medium">Success:</span> {statusChangeSuccess}
                </Alert>
            )}


            {downloadError && (
                <Alert color="failure" onDismiss={() => setDownloadError(null)} className="mb-4">
                    <span className="font-medium">Download/Preview Error:</span> {downloadError}
                </Alert>
            )}

            {loading ? (
                <div className="flex justify-center items-center p-4">
                    <Spinner size="md" /> <span className="ml-2 text-sm">Loading Documents...</span>
                </div>
            ) : error ? (
                <Alert color="failure" onDismiss={() => { /* Consider prop for dismiss if needed */ }} className="mb-4">
                    <span className="font-medium">Error loading documents:</span> {error}
                </Alert>
            ) : documents.length > 0 ? (
                <>


                    <table className="min-w-full divide-y divide-gray-200">
                        <thead className="bg-gray-50">
                            <tr>
                                <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                    Document name
                                </th>
                                <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                    Type
                                </th>
                                <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                    Size
                                </th>
                                <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                    Uploaded On
                                </th>
                                <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                        Status
                                </th>
                                <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                    Actions
                                </th>
                            </tr>
                        </thead>
                        <tbody className="bg-white divide-y divide-gray-200">
                            {documents.map((doc) => (
                                <tr key={doc.id}>
                                    <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                                        {doc.document_name}
                                    </td>
                                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                                        {doc.file_type}
                                    </td>
                                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                                        {formatFileSize(parseInt(doc.file_size, 10))}
                                    </td>
                                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                                        {new Date(doc.created_at).toLocaleDateString()}
                                    </td>
                                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                                            {doc.verified_status == STATUS_UNVERIFIED ? (
                                                <button
                                                    className={`py-1 px-3 rounded-lg text-white text-xs font-medium transition duration-150 ${statusChangeLoadingId === doc.id || previewLoadingId === doc.id || downloadLoadingId === doc.id ? 'bg-gray-400 cursor-not-allowed' : 'bg-yellow-500 hover:bg-yellow-600'}`}
                                                    onClick={() => handleVerifyUnverify(doc.id, 'verify')}
                                                    disabled={statusChangeLoadingId === doc.id || previewLoadingId === doc.id || downloadLoadingId === doc.id}
                                                >
                                                    {statusChangeLoadingId === doc.id ? (
                                                        <span className="flex items-center">
                                                            <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div> Changing verification stastus...
                                                        </span>
                                                    ) : 'Verify'}
                                                </button>
                                            ) : (
                                                <button
                                                    className={`py-1 px-3 rounded-lg text-white text-xs font-medium transition duration-150 ${statusChangeLoadingId === doc.id || previewLoadingId === doc.id || downloadLoadingId === doc.id ? 'bg-gray-400 cursor-not-allowed' : 'bg-blue-500 hover:bg-blue-600'}`}
                                                    onClick={() => handleVerifyUnverify(doc.id, 'unverify')}
                                                    disabled={statusChangeLoadingId === doc.id || previewLoadingId === doc.id || downloadLoadingId === doc.id}
                                                >
                                                    {statusChangeLoadingId === doc.id ? (
                                                        <span className="flex items-center">
                                                            <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div> Changing verification status...
                                                        </span>
                                                    ) : 'Verified'}
                                                </button>
                                            )}
                                    </td>
                                    <td className="px-6 py-4 whitespace-nowrap text-left text-sm font-medium">
                                        {/* VIEW BUTTON - updated disabled and spinner logic */}
                                        <button
                                            onClick={() => fetchAndHandleFile(doc.id, doc.original_file_name, 'preview')}
                                            className="text-blue-600 hover:text-blue-900 mr-4 cursor-pointer focus:outline-none"
                                            disabled={previewLoadingId === doc.id || downloadLoadingId === doc.id} // Disable if either preview or download is active for this doc
                                            type="button"
                                        >
                                            {previewLoadingId === doc.id ? <Spinner size="sm" className="inline-block mr-1" /> : ''} View
                                        </button>
                                        {/* DOWNLOAD BUTTON - updated disabled and spinner logic */}
                                        <button
                                            onClick={() => fetchAndHandleFile(doc.id, doc.original_file_name, 'download')}
                                            className="text-green-600 hover:text-green-900 mr-4 cursor-pointer focus:outline-none"
                                            disabled={downloadLoadingId === doc.id || previewLoadingId === doc.id} // Disable if either download or preview is active for this doc
                                            type="button"
                                        >
                                            {downloadLoadingId === doc.id ? <Spinner size="sm" className="inline-block mr-1" /> : ''} Download
                                        </button>
                                        <button
                                            onClick={() => onDeleteDocument(doc.id, doc.document_name)}
                                            className="text-red-600 hover:text-red-900 cursor-pointer focus:outline-none"
                                            type="button"
                                        >
                                            Delete
                                        </button>
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>

                    <div className="flex overflow-x-auto sm:justify-center mt-6">
                <Pagination
                    currentPage={pagination.current_page}
                    totalPages={pagination.last_page}
                    onPageChange={onPageChange}
                    showIcons
                />
            </div>
            <div className="text-center mt-2 text-sm text-gray-600 dark:text-gray-400">
                Showing {pagination.from} to {pagination.to} of {pagination.total} entries
            </div>
                </>
            ) : (
                <p className="">No documents available for this customer.</p>
            )}


            <UploadCustomerDocument
                isOpen={isUploadModalOpen}
                onClose={() => setIsUploadModalOpen(false)}
                customerId={customerId}
            />


        </>
    );
};

export default CustomerDocumentListing;